import React from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@src/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { ArcSocketContext } from '@src/core/socket/base';
import { ChannelResponseEnum } from '@src/core/socket/base/types';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  baseNotificationsApi,
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '../redux';
import type { IGetNotificationsResponse, INotificationNewFrame, INotificationUnreadCountFrame } from '../redux/types';
import { normalizeNotification } from '../redux/normalizers';

// Most-recent window fetched for the dropdown + "View all" modal. The modal
// paginates over this in-memory list; live WS frames prepend into it.
export const NOTIFICATIONS_FETCH_SIZE = 100;

// Stable args object so `updateQueryData` targets the exact cache entry.
const LIST_ARGS = { page: 0, size: NOTIFICATIONS_FETCH_SIZE } as const;

interface UseNotificationsFeedArgs {
  // Defer the list request until the user first opens the panel (the badge
  // relies only on the always-on unread-count query, per the recommended flow).
  listEnabled: boolean;
}

export const useNotificationsFeed = ({ listEnabled }: UseNotificationsFeedArgs) => {
  const dispatch = useDispatch<AppDispatch>();
  const { subscribe, unsubscribe } = React.useContext(ArcSocketContext);

  const { data: unreadCount = 0 } = useGetUnreadCountQuery();
  const { data: list, isLoading, isFetching } = useGetNotificationsQuery(
    listEnabled ? LIST_ARGS : skipToken,
  );

  const [markNotificationRead] = useMarkNotificationReadMutation();
  const [markAllNotificationsRead] = useMarkAllNotificationsReadMutation();

  const notifications = list?.data ?? [];

  // ── Cache writers ─────────────────────────────────────────────────────────
  // The badge reads the `getUnreadCount` cache; the dropdown/modal read the
  // `getNotifications` list cache. These helpers keep both in sync locally so
  // the UI reacts instantly, ahead of the server round-trip / WS echo.
  const patchList = React.useCallback(
    (recipe: (draft: IGetNotificationsResponse) => void) => {
      dispatch(baseNotificationsApi.util.updateQueryData('getNotifications', LIST_ARGS, recipe));
    },
    [dispatch],
  );

  const setUnreadCount = React.useCallback(
    (next: number) => {
      dispatch(baseNotificationsApi.util.updateQueryData('getUnreadCount', undefined, () => Math.max(0, next)));
    },
    [dispatch],
  );

  const decrementUnreadCount = React.useCallback(() => {
    dispatch(baseNotificationsApi.util.updateQueryData('getUnreadCount', undefined, (count) => Math.max(0, count - 1)));
  }, [dispatch]);

  // ── Live updates over the shared WebSocket (one handler per channel) ───────
  React.useEffect(() => {
    subscribe(ChannelResponseEnum.NOTIFICATION_NEW, (frame: INotificationNewFrame) => {
      if (typeof frame.unreadCount === 'number') setUnreadCount(frame.unreadCount);
      if (!frame.notification) return;

      const incoming = normalizeNotification(frame.notification as Record<string, unknown>);
      patchList((draft) => {
        if (draft.data.some((n) => n.id === incoming.id)) return;
        draft.data.unshift(incoming);
        if (draft.data.length > NOTIFICATIONS_FETCH_SIZE) draft.data.pop();
      });
    });

    subscribe(ChannelResponseEnum.NOTIFICATION_UNREAD_COUNT, (frame: INotificationUnreadCountFrame) => {
      if (typeof frame.unreadCount === 'number') setUnreadCount(frame.unreadCount);
    });

    return () => {
      unsubscribe(ChannelResponseEnum.NOTIFICATION_NEW);
      unsubscribe(ChannelResponseEnum.NOTIFICATION_UNREAD_COUNT);
    };
  }, [subscribe, unsubscribe, setUnreadCount, patchList]);

  // ── User actions (optimistic, reconciled by the WS echo / refetch) ─────────
  const markRead = React.useCallback(
    (id: number) => {
      const target = notifications.find((n) => n.id === id);
      if (!target || target.read) return;

      patchList((draft) => {
        const item = draft.data.find((n) => n.id === id);
        if (item) item.read = true;
      });
      decrementUnreadCount();

      markNotificationRead(id)
        .unwrap()
        .catch((error: unknown) => {
          trackError(toTrackedError(error), { code: 'FE_0018', extra: { flow: 'markNotificationRead', id } });
        });
    },
    [notifications, patchList, decrementUnreadCount, markNotificationRead],
  );

  const markAllAsRead = React.useCallback(() => {
    markAllNotificationsRead()
      .unwrap()
      .then((nextCount) => {
        patchList((draft) => {
          draft.data.forEach((n) => {
            n.read = true;
          });
        });
        setUnreadCount(typeof nextCount === 'number' ? nextCount : 0);
      })
      .catch((error: unknown) => {
        trackError(toTrackedError(error), { code: 'FE_0018', extra: { flow: 'markAllNotificationsRead' } });
      });
  }, [markAllNotificationsRead, patchList, setUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading: isLoading || isFetching,
    markRead,
    markAllAsRead,
  };
};
